import { ActivatedRouteSnapshot, CanActivateFn, DeprecatedGuard, RouterStateSnapshot } from '@angular/router';
import { NavigationItem } from '@claas/claas-ui-components/navigation/navigation';
import { combineLatest, map, Observable } from 'rxjs';
import { AuthGuard } from '@cde-fe-app/app/shared/guard/auth.guard';
import { createFeatureToggleGuard } from '@cde-fe-app/app/shared/guard/feature-toggle-guard.factory';
import { MACHINE_ADVANCED_PERMISSION, MACHINE_MANAGE_PERMISSION, PLATTFORM_ADVANCED_PERMISSION } from './permission';
import { ISidenavItem } from '../components/cde-sidenav/models/sidenav.model';
import { Features } from '../models/feature-toggle.model';

export interface IFeatureToggleNavigationItem extends NavigationItem {
  featureToggleDescription?: FeatureToggleDescription;
  children?: IFeatureToggleNavigationItem[];
}

export const navigationItems: IFeatureToggleNavigationItem[] = [
  {
    label: 'dashboard.widgets.notifications.title',
    route: 'notifications',
    hidden: true,
    iconNamespace: 'cc',
  },
  {
    label: 'settings.legal_texts',
    route: 'privacy',
    hidden: true,
    iconNamespace: 'cc',
  },
  {
    label: 'settings.support',
    route: 'support',
    hidden: true,
    iconNamespace: 'cc',
  },
  {
    label: 'left_sidenav.dashboard',
    route: 'dashboard',
    activeRoutes: ['dashboard'],
    iconId: '648421',
    iconNamespace: 'cc',
  },
  {
    label: 'left_sidenav.machines',
    route: 'machines',
    activeRoutes: ['machines'],
    matching: {
      queryParams: 'ignored',
    },
    iconId: '000131',
    iconNamespace: 'cc',
    children: [
      {
        label: 'machine_details.machine_overview',
        route: 'machines/overview',
        activeRoutes: ['machines/overview', 'machines/details', 'machines/add'],
        iconId: '649042',
        iconNamespace: 'cc',
      },
      {
        label: 'machine_sharing.title',
        route: 'machines/machine-sharing',
        activeRoutes: ['machines/machine-sharing'],
        matching: {
          queryParams: 'ignored',
        },
        iconId: '648569',
        iconNamespace: 'cc',
        featureToggleDescription: { feature: Features.MACHINE_SHARING },
      },
      {
        label: 'machine_details.device-management',
        route: 'machines/device-manager',
        activeRoutes: ['machines/device-manager'],
        matching: {
          queryParams: 'ignored',
        },
        iconId: '776898',
        iconNamespace: 'cc',
      },
    ],
    featureToggleDescription: {
      alternativeRoute: {
        label: 'left_sidenav.machines',
        route: 'machines',
        activeRoutes: ['machines'],
        iconId: '000131',
        iconNamespace: 'cc',
      },
      operator: 'OR',
    },
  },
  {
    label: 'left_sidenav.analytics',
    route: 'analytics',
    activeRoutes: ['analytics'],
    matching: {
      queryParams: 'ignored',
    },
    iconId: '648429',
    iconNamespace: 'cc',
    children: [
      {
        label: 'left_sidenav.analytics_overview',
        route: 'analytics/table',
        activeRoutes: ['analytics/table', 'analytics/details', 'analytics/details/:id/:lastTransmissionAt'],
        iconId: '649042',
        iconNamespace: 'cc',
      },
      {
        label: 'left_sidenav.analytics_reports',
        route: 'analytics/reports',
        activeRoutes: [
          'analytics/reports',
          'analytics/reports/daily-reports/:id',
          'analytics/reports/createReport',
          'analytics/reports/campaign-report/:id',
        ],
        matching: {
          queryParams: 'ignored',
        },
        iconId: '000775',
        iconNamespace: 'cc',
      },
      {
        label: 'left_sidenav.analytics_comparison',
        route: 'analytics/comparison',
        activeRoutes: ['analytics/comparison'],
        matching: {
          queryParams: 'ignored',
        },
        iconId: '001514',
        iconNamespace: 'cc',
      },
    ],
  },
  {
    label: 'file_transfer.title',
    route: 'file-transfer',
    activeRoutes: ['file-transfer'],
    iconId: '659779',
    iconNamespace: 'cc',
  },
  {
    label: 'left_sidenav.shops',
    route: 'shops',
    activeRoutes: ['shops'],
    iconId: '648469',
    iconNamespace: 'cc',
    featureToggleDescription: { feature: Features.SAP },
  },
  {
    label: 'left_sidenav.parts_shop',
    route: 'parts-shop',
    activeRoutes: ['parts-shop'],
    iconId: '1171368',
    iconNamespace: 'cc',
    featureToggleDescription: { feature: Features.NEW_PARTS_SHOP },
  },
  {
    label: 'license.title',
    route: 'license',
    activeRoutes: ['license'],
    iconId: '001414',
    iconNamespace: 'cc',
    featureToggleDescription: { feature: Features.SAP },
  },
  {
    label: 'left_sidenav.config',
    route: 'configurations',
    activeRoutes: ['configurations'],
    iconId: '017978',
    iconNamespace: 'cc',
    featureToggleDescription: { feature: [Features.CONFIGURATOR, Features.SAP] },
  },
  {
    label: 'settings.connection_manager',
    route: 'connections',
    activeRoutes: ['connections'],
    iconId: '001612',
    iconNamespace: 'cc',
  },
  {
    label: 'agin_connection_manager.title',
    route: 'agin-connections',
    activeRoutes: ['agin-connections'],
    iconId: '418798',
    iconNamespace: 'cc',
    featureToggleDescription: { feature: Features.AGIN_CONNECTION_MANAGER },
  },
];

export const goBackNavItems: ISidenavItem[] = [
  { text: 'left_sidenav.dashboard', route: '/dashboard' },
  { text: 'machine_details.machine_overview', route: '/machines/overview' },
  { text: 'go_back_nav.machine_details', route: '/machines/details' },
  { text: 'machine_details.machine_overview', route: '/machines' },
  { text: 'go_back_nav.analysis_create_report', route: '/analytics/reports/createReport' },
  { text: 'go_back_nav.analysis_reports', route: '/analytics/reports' },
  { text: 'go_back_nav.analysis_overview', route: '/analytics/details' },
  { text: 'analytics.machine_comparison.title', route: '/analytics/comparison' },
  { text: 'analytics.title', route: '/analytics' },
  { text: 'left_sidenav.config', route: '/configurations' },
  { text: 'left_sidenav.shops', route: '/shops' },
  { text: 'file_transfer.new_transfer', route: '/file-transfer' },
  { text: 'settings.connection_manager', route: '/connections' },
  { text: 'settings.legal_texts', route: '/privacy' },
  { text: 'settings.support', route: '/support' },
  { text: 'left_sidenav.dashboard', route: '/dashboard' },
  { text: 'file_transfer.new_transfer', route: '/file-transfer/new-file-transfer' },
  { text: 'file_transfer.title', route: '/file-transfer' },
  { text: 'settings.connection_manager', route: '/connections' },
  { text: 'agin_connection_manager.title', route: '/agin-connections' },
  { text: 'left_sidenav.shops', route: '/parts-shop' },
];

export const permissionsPerRoute = new Map<string, string[]>([
  ['analytics', MACHINE_ADVANCED_PERMISSION],
  ['analytics/table', MACHINE_ADVANCED_PERMISSION],
  ['analytics/reports', MACHINE_MANAGE_PERMISSION],
  ['analytics/comparison', MACHINE_ADVANCED_PERMISSION],
  ['shops', PLATTFORM_ADVANCED_PERMISSION],
  ['license', MACHINE_MANAGE_PERMISSION],
  ['configurations', PLATTFORM_ADVANCED_PERMISSION],
  ['machines/device-manager', MACHINE_MANAGE_PERMISSION],
]);

export interface FeatureToggleDescription {
  feature?: string | string[];
  alternativeRoute?: NavigationItem;
  operator?: 'AND' | 'OR';
}

type FeatureTogglePerRoute = [FeatureToggleDescription, IFeatureToggleNavigationItem[]];
const featureTogglesPerRoute = navigationItems.reduce(
  (acc, item) => {
    if (item?.route && item.featureToggleDescription) {
      acc[item.route] = [item.featureToggleDescription, item.children ?? []];
    }
    return acc;
  },
  {} as Record<string, FeatureTogglePerRoute>
);

export function createFeatureToggleAuthGuardsPerRoute(route: string): (CanActivateFn | DeprecatedGuard)[] {
  if (!featureTogglesPerRoute[route]) {
    return [AuthGuard];
  }

  const [featureToggles, children] = featureTogglesPerRoute[route] as FeatureTogglePerRoute;
  // alternative route is only used if the feature is untoggled and can be used
  // as a fallback always with no guard
  if (featureToggles.alternativeRoute) {
    return [AuthGuard];
  }

  const features = getFeatures(featureToggles, children);

  const guards = features.map(feature => createFeatureToggleGuard(feature as Features));
  if (featureToggles.operator == null || featureToggles.operator === 'AND') {
    return [...guards, AuthGuard];
  }

  return [generateOrCombinedGuard(guards), AuthGuard];
}

export function getFeatures(
  featureToggleDescription: FeatureToggleDescription,
  children: IFeatureToggleNavigationItem[]
) {
  if (featureToggleDescription.feature != null) {
    return Array.isArray(featureToggleDescription.feature)
      ? featureToggleDescription.feature
      : [featureToggleDescription.feature];
  }

  return (
    children
      .map(child => child.featureToggleDescription)
      .filter(ftDesc => ftDesc != null)
      .map(ftDesc => ftDesc.feature)
      .filter(feature => feature != null)
      .flat() ?? []
  );
}

function generateOrCombinedGuard(guards: CanActivateFn[]): CanActivateFn {
  return (_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
    const guardsResults = guards.map(guard => guard(_route, _state) as Observable<boolean>);
    return combineLatest(guardsResults).pipe(map(results => results.some(result => result)));
  };
}
